import {
  Host,
  OnInit,
  Optional,
  SkipSelf,
  ViewChild,
  Component,
  ElementRef,
  forwardRef, Input,
} from '@angular/core';

import { ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { BehaviorSubject } from 'rxjs';

import {BaseControlComponent, Helper, PhoneAutocompleteControl} from '../../classes';

import { PhoneAutocompleteCountryInterface, PhoneAutocompleteValueInterface, PhoneCodeOptionInterface } from './interfaces';
import { Language } from '../../language';

@Component({
  selector: 'app-phone-autocomplete',
  templateUrl: Helper.getPortalTemplate(),
  styleUrls: [Helper.getPortalStyle()],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PhoneAutocompleteComponent),
    multi: true,
  }],
})
export class PhoneAutocompleteComponent
  extends BaseControlComponent<PhoneAutocompleteValueInterface, PhoneAutocompleteValueInterface, PhoneAutocompleteControl>
  implements OnInit {

  private readonly phoneCodeMaxLength: number;

  private countries: PhoneAutocompleteCountryInterface[];
  private codes: PhoneCodeOptionInterface[];

  public selectedOption: PhoneCodeOptionInterface | null;
  public filteredCodes: BehaviorSubject<PhoneCodeOptionInterface[]> | undefined;

  @Input() public outlined: boolean;
  @Input() public currenLanguage: string;
  @Input() public codePlaceholder: string | undefined;
  @Input() public phoneNumberPlaceHolder: string | undefined;

  @ViewChild('autocompleteInput', { static: true }) autocompleteInputReference: ElementRef | undefined;
  @ViewChild('autocomplete', { static: true }) autocompleteReference: MatAutocomplete | undefined;
  @ViewChild('input', { static: true }) inputReference: ElementRef | undefined;

  constructor(
    @Optional() @Host() @SkipSelf()
    protected controlContainer: ControlContainer,
  ) {
    super(controlContainer);

    this.codes = [];
    this.countries = [];
    this.outlined = false;
    this.selectedOption = null;
    this.phoneCodeMaxLength = 5;
    this.initValue();
  }

  private initValue(): void {
    this.value = {
      code: null,
      phone: '',
    };
  }

  private initPhoneCodeOptions(): PhoneCodeOptionInterface[] {
    this.countries = this.asFormsControl().countries;

    return this.countries
      .sort((current: PhoneAutocompleteCountryInterface, next: PhoneAutocompleteCountryInterface) => {
        const currentName = current.name;
        const nextName = next.name;

		  if (currentName < nextName) {
          return -1;
        }

        if (currentName > nextName) {
          return 1;
        }

        return 0;
      })
      .filter((code: PhoneAutocompleteCountryInterface) => code.phoneCode)
      .map((code: PhoneAutocompleteCountryInterface) => {
        return {
          label: `(${ code.phoneCode })`,
          value: code.phoneCode,
          classes: this.countryIconClassPrefix + code.id.toLowerCase(),
        };
      });
  }

  ngOnInit(): void {
    if (!this.value) {
      this.initValue();
    }

    this.codes = this.initPhoneCodeOptions();

    if (this.codes && this.codes.length > 0) {
      this.filteredCodes = new BehaviorSubject(this.codes);
    }

    this.checkInvalid();
  }

  filterCodes(event: Event): void {
    const value = (event.target as HTMLInputElement).value;

    this.executeFilter(value);
  }

  filterCodesOnPaste(event: ClipboardEvent): void {
    const value = (event.clipboardData as DataTransfer).getData('text');

    this.executeFilter(value);
  }

  executeFilter(value: string): boolean {
    const pattern = value.toLowerCase().replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

    const regex = new RegExp('.*' + pattern + '.*');

    const filteredCodes = this.codes.filter((code: PhoneCodeOptionInterface) => regex.test(code.label.toLowerCase()));

    this.filteredCodes?.next(filteredCodes);

    /**
     * Set country if in the array have only one suggestion.
     */

    if ('code' in this.value!) {
      if (filteredCodes.length !== 1) {
        this.asFormsControl().resetValue();
        this.changeValue(null);
        this.selectedOption = null;
      } else {
        if (filteredCodes[0].value !== value) {
          this.asFormsControl().resetValue();
          this.changeValue(null);
          this.selectedOption = null;
        } else {
          this.value.code = filteredCodes[0].value;
          this.changeValue(this.value);
        }
      }
    }


    return true;
  }

  enterCode(event: MatAutocompleteSelectedEvent): void {
    if (this.autocompleteInputReference) {
      this.autocompleteInputReference.nativeElement.value = event.option.viewValue;
    }

    if ('code' in this.value!) {
      this.value.code = event.option.value;
    }

    const filteredCodes = this.codes.find((code: PhoneCodeOptionInterface) => {
      return event.option.value === code.value;
    });

    this.selectedOption = filteredCodes ?? null;

    this.changeValue(this.value);
  }

  enterPhone(event: Event): boolean {
    const target = event.target as HTMLInputElement;

    const initialValue = target.value;
    const strippedValue = initialValue.replace(/[^0-9]/g, '');

    if (initialValue !== strippedValue) {
      target.value = strippedValue;

      return false;
    }

    if ('phone' in this.value!) {
      this.value.phone = initialValue;
    }

    this.changeValue(this.value);

    return true;
  }

  changeValue(value: PhoneAutocompleteValueInterface | PhoneAutocompleteValueInterface[] | undefined | null): void {
    if (this.onChange && !Array.isArray(value)) {
      this.onChange(value as PhoneAutocompleteValueInterface);
    }

    this.checkInvalid();
    this.control!.resetErrors();

    if (value) {
      super.writeValue(value);
    }
  }

  writeValue(value: PhoneAutocompleteValueInterface): void {
    if (value) {
      let filteredCodes: PhoneCodeOptionInterface[] = [];

      if (this.codes) {
        filteredCodes = this.codes.filter((code: PhoneCodeOptionInterface) => {
          return value.code === code.value;
        });
      }

      if (filteredCodes.length !== 0) {
        this.selectedOption = filteredCodes[0];
        this.autocompleteInputReference!.nativeElement.value = filteredCodes[0].label;

        this.inputReference!.nativeElement.value = value.phone;

        super.writeValue({
          code: filteredCodes[0].value,
          phone: value.phone,
        });
      } else {
        this.selectedOption = null;
        this.autocompleteInputReference!.nativeElement.value = null;
        this.inputReference!.nativeElement.value = '';

        super.writeValue({
          code: null,
          phone: '',
        });
      }
    }

    this.checkInvalid();
    this.control!.resetErrors();
  }

  asFormsControl(): PhoneAutocompleteControl {
    if (!this.control) {
      this.control = new PhoneAutocompleteControl({
        name: '',
        value: {
          code: null,
          phone: '',
        },
        placeholder: '',
        validators: [],
        componentType: 0,
        invalid: false,
        errors: [],
        countries: [],
      });
    }

    if ((typeof this.phoneNumberPlaceHolder !== 'undefined') && this.control) {
      this.control.placeholder = this.phoneNumberPlaceHolder;
    }

    return this.control as PhoneAutocompleteControl;
  }

  trackByCode(index: number, item: PhoneCodeOptionInterface): string {
    return item.value;
  }
}
